import { isValidElement } from 'react'

/**
 * Produces a minimal text representation of the child structure, without showing props or keys on child elements.
 * Null, undefined and numbers are all represented in the output.
 *
 * e.g. for this JSX:
 *
 * ```
 * <BodyText>
 *   Hello <b>world! <SomeComponent>How <i>are</i> you?</SomeComponent></b>{null}{undefined}{5}
 * </BodyText>
 * ```
 *
 * calling `stringifyChildrenStructure` on the children of `BodyText` would return "['Hello ',<>['world! ',<>['How ',<>'are'</>,' you?']</>]</>,null,undefined,5]"
 */
export function stringifyChildrenStructure(children: React.ReactNode): string {
  if (children === null) return 'null'
  if (children === undefined) return 'undefined'
  if (typeof children === 'boolean') return String(children)
  if (typeof children === 'string') return `'${children}'`
  if (typeof children === 'number') return String(children)

  if (isValidElement<{ children?: React.ReactNode }>(children)) {
    const nestedChildren = children.props.children
    return nestedChildren === undefined ?
      '<></>' :
      `<>${stringifyChildrenStructure(children.props.children)}</>`
  }

  if (Array.isArray(children)) {
    return `[${children.map(stringifyChildrenStructure).join(',')}]`
  }

  return 'unknown'
}
